import * as THREE from 'three';
import * as MATH from './mymath.js';
import { CSS2DRenderer, CSS2DObject } from './CSS2DRenderer.js';
import { TrackballControls } from './trackball.js';
import { Colors } from './colors.js';
import { Simplex } from './geometry.js';
import './css/tailwind/before.css';
import './css/style.css';
import './css/tailwind/after.css';


function createControls( camera ) {

    let controls = new TrackballControls( camera, renderer.domElement );

    controls.rotateSpeed = 4.0;
    controls.zoomSpeed = 1.5;
    controls.panSpeed = 0.8;

    controls.keys = [ 65, 83, 68 ];
    return controls;

}


class RegionNums {
    constructor ( numRegions ) {
        this.numRegions = numRegions;
    }
    
    generateMathJax ( ) {
        const mathjaxi = document.createElement( 'div' );
		mathjaxi.className = 'mathjaxi';
		mathjaxi.id = 'mathjaxi';
        mathjaxi.style.display = 'none';
        for ( let i = 0; i < this.numRegions; i++ ) {
            const label = document.createElement( 'div' );
            const num = document.createElement( 'div' );
            num.className = 'region-num';
            num.innerText = 'R';
            const sub = document.createElement( 'sub' );
            sub.innerText = i;
            label.id = 'regionNum' + i;
            num.appendChild(sub);
            label.appendChild(num);
            label.className = 'region-num-label';
            mathjaxi.appendChild(label);
        }
		return mathjaxi;
    }
}


class RegionLabel {
    constructor ( bitstring, colors, number ) {
        this.bitstring = bitstring;
        this.colors = colors;
        this.number = number;
        this.perm = this.decodeBitstring();
    }

    // Convert bitstring encoding permutation as an inversion set
    // with n choose 2 bits to to a permutation [n] representation
    decodeBitstring ( ) {
        // n choose 2 is n * (n - 1) / 2
        // so 2 * permlength = n^2 - n
        // so sqrt(2 * permlength) = n * sqrt(1 - 1/n)
        // For n >=2 Math.ceil(sqrt(1 - 1/n)) = 1.
        const permLength = Math.floor(Math.sqrt(this.bitstring.length * 2)) + 1;
        let perm = MATH.range(permLength);
        let idx = 0;
        // In what follows we effectively undo the inversion
        // Hence we sort.
        // What we obtain is the inverse permutation
        for ( let i = 0; i < permLength; i++ ) {
            for ( let j = i + 1; j < permLength; j++ ) {
                if ( parseInt(this.bitstring[idx]) ) {
                    // Swap elements that are out of order
                    perm[i]++;
                    perm[j]--;
                }
                idx++;
            }
        }
        // Compute inverse of inverse permutation.
        let out = Array(permLength);
        for ( let i = 0; i < permLength; i++ ) {
            out[perm[i]] = i;
        }
        return out;
    }

    buildLabel ( ) {
        const order = this.perm;
        const label = document.createElement( 'div' );
        label.className = 'region-label';

        const num = document.getElementById('regionNum' + this.number).cloneNode(true);
        // num.style.visibility = 'visible';
        label.appendChild(num);
        // Reverse order of colours as we read left to right..
        // This means max is at left
        const circleSizeClasses = ['circle-1', 'circle-2', 'circle-3', 'circle-4']
        let idx = 0;
        for ( const i of order.reverse() ) {
            const circle = document.createElement( 'div' );
            circle.className = 'circle ' + circleSizeClasses[idx];
            let color = new THREE.Color(this.colors[i]);
            circle.style.backgroundColor = '#' + color.getHexString();
            label.appendChild(circle);
            idx++;
        }
        return label;
    }
}


class Dataset {
    constructor (positions, scale) {
        this.positions = positions;
        this.scale = scale;
        const geometry = new THREE.SphereGeometry(this.scale, 4, 4);
        const material = new THREE.MeshLambertMaterial( { color: 0xff5555, transparent: true } );

        this._points = new THREE.Group();
        this.points = this._points.children;
        for (let i = 0; i < this.positions.length; i++) {

            const [x, y, z] = this.positions[i];
            let sphere = new THREE.Mesh(geometry, material);

            sphere.position.x = x;
            sphere.position.y = y;
            sphere.position.z = z;

            // Allow each point to be colored independently
            sphere.material = sphere.material.clone();

            this._points.add( sphere );
        }
    }

    length ( ) {
        return this.points.length;
    }

    add_to ( scene ) {
        scene.add(this._points);
    }
}


class Permutohedron {
    constructor (position, dim) {

        this.position = position;
        this.dim = dim;

        if ( this.dim == 4 ) {
			this.scale = Math.sqrt(5);
            this.positions = [[ 0.,  1.,  2.],
                              [ 1.,  0.,  2.],
                              [ 2.,  0.,  1.],
                              [ 2., -1.,  0.],
                              [ 2.,  0., -1.],
                              [ 2.,  1.,  0.],
                              [ 1.,  2.,  0.],
                              [ 0.,  2.,  1.],
                              [-1.,  2.,  0.],
                              [ 0.,  2., -1.],
                              [ 0.,  1., -2.],
                              [ 1.,  0., -2.],
                              [ 0., -1., -2.],
                              [-1.,  0., -2.],
                              [-2.,  0., -1.],
                              [-2.,  1.,  0.],
                              [-2.,  0.,  1.],
                              [-2., -1.,  0.],
                              [-1., -2.,  0.],
                              [ 0., -2., -1.],
                              [ 1., -2.,  0.],
                              [ 0., -2.,  1.],
                              [ 0., -1.,  2.],
                              [-1.,  0.,  2.]];
            this.faces = [ 0, 1, 2,
                           1, 3, 2,
                           0, 2, 5,
                           0, 5, 7,
                           6, 7, 5,
                           6, 10, 9,
                           6, 11, 10,
                           6, 4, 11,
                           6, 5, 4,
                           5, 2, 3,
                           5, 3, 4,
                           1, 20, 3,
                           1, 21, 20,
                           1, 22, 21,
                           0, 22, 1,
                           0, 23, 22,
                           6, 9, 7,
                           9, 8, 7,
                           9, 15, 8,
                           9, 14, 15,
                           9, 13, 14,
                           9, 10, 13,
                           13, 10, 11,
                           13, 11, 12,
                           11, 4, 3,
                           11, 3, 20,
                           11, 20, 19,
                           11, 19, 12,
                           18, 17, 14,
                           18, 14, 13,
                           18, 13, 12,
                           18, 12, 19,
                           14, 17, 16,
                           14, 16, 15,
                           19, 20, 21,
                           19, 21, 18,
                           21, 22, 23,
                           21, 23, 16,
                           21, 16, 17,
                           21, 17, 18,
                           16, 8, 15,
                           16, 7, 8,
                           16, 0, 7,
                           16, 23, 0 ];
        }
        else if ( dim == 3 ) {
			this.scale = Math.sqrt(1.5);
            this.positions = [[ -1.,  0.,  1.],
                              [ 0.,  -1.,  1.],
                              [ 1.,  -1.,  0.],
                              [ 1.,  0.,  -1.],
                              [ 0.,  1.,  -1.],
                              [ -1.,  1.,  0.]];
            this.faces = [ 0, 1, 5,
                           5, 1, 4,
                           4, 1, 3,
                           3, 1, 2];
        }
		else {
			this.scale = Math.sqrt(2);
            this.positions = [[ -1.,  0.,  1.],
                              [ 1.,  0.,  -1.]];
            this.faces = [];
		}
        const geometry = new THREE.SphereGeometry(.18, 8, 8);
        const material = new THREE.MeshLambertMaterial( { color: 0x55cc55, transparent: true } );
        this.group = new THREE.Group();
        this.labels = new THREE.Group();
        this.vertices = new THREE.Group();
        for (let i = 0; i < this.positions.length; i++) {

            const [x, y, z] = this.positions[i];
            let sphere = new THREE.Mesh(geometry, material);

            sphere.position.x = x;
            sphere.position.y = y;
            sphere.position.z = z;

            // Allow each vertex to be colored independently
            sphere.material = sphere.material.clone();

            this.vertices.add( sphere );
        }
        this.vertexLookup = this.computeLabels();
        this.group.add(this.vertices);
        this.group.add(this.labels);
        this.group.add(this.construct_polytope());
    }

    computeLabels () {
        // Normal vectors of the braid arrangement
        let normals;
        if ( this.dim == 4 ) {
            normals = [[1, 1, 0],
					   [1, 0, 1],
					   [0, 1, 1],
					   [0, -1, 1],
					   [-1, 0, 1],
					   [-1, 1, 0]];
            // normals = [[1, -1, 0],
			// 		   [1, 0, -1],
			// 		   [0, -1, -1],
			// 		   [0, 1, -1],
			// 		   [-1, 0,-1],
			// 		   [-1, -1,0]];
        }
        else if ( this.dim == 3 ) {
            normals = [[1, -1, 0],
                       [1, 0, -1],
                       [0, 1, -1]];
        }
        else {
            normals = [[1, -1, 0]];
		}

        // Assuming no duplicates here
        let signVectors = [];
        let idx = 0;
        for (let p of this.positions) {
            const [px, py, pz] = p;
            let sign_vector = [];
            normals.forEach(function (n) {
                const [nx, ny, nz] = n;
                let dot = px * nx + py * ny + pz * nz;
                if ( dot > 0 ) {
                    sign_vector.push( 1 );
                }
                else {
                    sign_vector.push( 0 );
                }
            });
            let str_sign_vector = sign_vector.join('');
            signVectors.push(str_sign_vector);

            // const text = document.createElement( 'div' );
            // text.className = 'label';
            // text.style.color = '#000000';
            // text.textContent = '$$R_{' + idx + '}$$';
            // text.style.visibility = 'hidden';
			const num = document.getElementById('regionNum' + idx).cloneNode(true);
			// num.style.visibility = 'visible';

            let label = new CSS2DObject( num );
            label.position.copy(new THREE.Vector3(p[0], p[1], p[2]));
            this.labels.add(label);
            idx += 1;
        }

        let vertexLookup = {};
        this.vertexNumber = {};
        this.vertexLabel = {};
        for ( let i = 0; i < this.vertices.children.length; i++ ) {
            let v = this.vertices.children[i];
            vertexLookup[signVectors[i]] = v;
            this.vertexNumber[signVectors[i]] = i;
            this.vertexLabel[v.uuid] = this.labels.children[i];
        }
        return vertexLookup;
    }

    color_vertex( v, color, opacity, visible=true ) {
        const tcolor = new THREE.Color(color);
        v.material.color = tcolor;
        v.material.opacity = opacity;
        let label = this.vertexLabel[v.uuid];
        label.element.style.color = '#' + tcolor.getHexString();
        // v.visible = visible;
        label.visible = visible;
    }

    color_vertices( color, opacity, visible=true ) {
        for ( let i = 0; i < this.vertices.children.length; i++ ) {
            this.color_vertex(this.vertices.children[i], color, opacity, visible);
        }
    }

    construct_polytope() {

        // let materials = [];
        // for (let i = 0; i < geometry.faces.length; i++) {
        //     const color = parseInt(Math.random() * 0xffffff);
        //     let face = geometry.faces[i];
        //     face.color.setHex(color);
        //     // materials.push(new THREE.MeshBasicMaterial());
        // }
        let positions = [].concat(...this.positions);
        const geometry = new THREE.PolyhedronBufferGeometry( positions, this.faces, this.scale, 0 );
        const material = new THREE.MeshNormalMaterial({flatShading: true, transparent: true, opacity: 0.25});
        // const material = new THREE.MeshPhysicalMaterial({ transparent: true, opacity: 0.5, reflectivity: 0., side: THREE.BackSide});
        let object = new THREE.Mesh(geometry, material);

        const [x, y, z] = this.position;
        object.translateX(x);
        object.translateY(y);
        object.translateZ(z);

        return object;
    }

    add_to ( scene ) {
        scene.add(this.group);
    }
}


class Hyperplane {
    constructor (x, y, z, color, width=10, height=10, opacity=1.) {
        this.x = x;
        this.y = y;
        this.z = z;
        this.color = color;
        const material = new THREE.MeshPhysicalMaterial( {color: color, side: THREE.DoubleSide, transparent:true, opacity: opacity} );
        const geometry = new THREE.PlaneGeometry(width, height);
        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.lookAt(new THREE.Vector3(x, y, z));
    }

    add_to (scene) {
        scene.add(this.mesh);
    }

    look_at (dir) {
        this.mesh.lookAt(dir);
    }
}


class WeightVector {
    constructor (x, y, z, color=0x00ff00, label) {
        this.x = x;
        this.y = y;
        this.z = z;
        this.color = color;
        const end = new THREE.Vector3( x, y, z );
        const dir = end.normalize();
        const origin = new THREE.Vector3( 0, 0, 0 );
        const length = this.length();
        this.head_length = .3;
        this.head_width = .15;
        this.arrow = new THREE.ArrowHelper( dir, origin, length, color, this.head_length, this.head_width);
        this.hyperplane = new Hyperplane(dir.x, dir.y, dir.z, color);

        const text = document.createElement( 'div' );
        text.className = 'w-label';
        const rep = this.rep_obj();
        text.style.color = '#' + rep.material.color.getHexString();
        text.textContent = label || 'w';

        this.label = new CSS2DObject( text );
        rep.getWorldPosition(this.label.position);

        this.group = new THREE.Group()
        this.group.add( this.arrow );
        this.hyperplane.mesh.visible = false;
        this.group.add( this.hyperplane.mesh );
        this.group.add( this.label );
    }

    length () {
        return Math.sqrt(this.x ** 2 + this.y ** 2 + this.z ** 2);
    }

    add ( other ) {
        const x = this.x + other.x;
        const y = this.y + other.y;
        const z = this.z + other.z;
        const this_color = new THREE.Color(this.color);
        const other_color = new THREE.Color(other.color);
        const comb_color = this_color.lerp(other_color, .5);
        const color = comb_color.getHex();
        return new WeightVector(x, y, z, color, this.label);
    }

    scale (value) {
        const x = this.x * value;
        const y = this.y * value;
        const z = this.z * value;
        return new WeightVector(x, y, z, this.color, this.label);
    }

    add_to (scene) {
        scene.add(this.group);
    }

    rep_obj () {
        return this.arrow.children[1];
    }

    set_hyperplane_visible () {
        this.hyperplane.mesh.visible = true;
    }

    move_to (pos) {
        let dir = new THREE.Vector3( pos.x, pos.y, pos.z);
        this.x = pos.x;
        this.y = pos.y;
        this.z = pos.z;
        dir = dir.normalize();
        this.arrow.setDirection(dir);
        this.arrow.setLength(this.length(), this.head_length, this.head_width);
        // Update location of label
        this.rep_obj().getWorldPosition(this.label.position);
        this.hyperplane.look_at(dir);
    }
}


class WeightQuiver {
    // A collection of weight vectors
    constructor ( weights ) {
        this.weights = weights;
        this.raycaster = new THREE.Raycaster();
        this._weights = new THREE.Group();

        for ( let i = 0; i < this.weights.length; i++ ) {
            let weight = this.weights[i];
            this._weights.add( weight.group );
        }
        this.braid_construction();
    }

    add (w) {
        this.weights.push(w);
    }

    add_to (scene) {
        scene.add(this._weights);
    }

    scene_candidates ( ) {
        const candidates = [];
        this.weights.forEach(function (e) {
            candidates.push(e.rep_obj());
        });
        return candidates;
    }

    find_rep (rep) {
        let found = null;
        this.weights.forEach(function (e) {
            if ( e.rep_obj().uuid === rep.uuid ) {
                found = e;
            }
        });
        if ( found == null ) {
            this.braid_weights.forEach(function (e) {
                if ( e.rep_obj().uuid === rep.uuid ) {
                    found = e;
                }
            });
        }
        return found;
    }

    pick(mouse, scene, camera) {
        let object = null;
        this.raycaster.setFromCamera(mouse, camera);

        const intersectedObjects = this.raycaster.intersectObjects(this.scene_candidates());
        if (intersectedObjects.length) {
            object = this.find_rep(intersectedObjects[0].object);
        }
        return object;
    }

    braid_construction () {
        this.braid_weights = [];
        this.braid_group = new THREE.Group();
        for ( let i = 0; i < this.weights.length; i++ ) {
            for ( let j = i + 1; j < this.weights.length; j++ ) {
                let diff = this.weights[i].add(this.weights[j].scale(-1.));
                let left = '<span style="color: #';
                left += this.weights[i].rep_obj().material.color.getHexString();
                left +=  ';">';
                left += String.fromCharCode(97 + i) + '</span>';
                let right = '<span style="color: #';
                right += this.weights[j].rep_obj().material.color.getHexString();
                right +=  ';">';
                right += String.fromCharCode(97 + j) + '</span>';
                diff.label.element.innerHTML = 'Class ' + left + ' > ' + right;
                diff.arrow.visible = true;
                diff.label.visible = false;
                diff.label.element.style.fontSize = '15px';
                diff.set_hyperplane_visible();
                this.braid_group.add(diff.group);
                this.braid_weights.push(diff);
            }
        }
        this._weights.add(this.braid_group);
    }

    braid_update () {
        let idx = 0;
        for ( let i = 0; i < this.weights.length; i++ ) {
            for ( let j = i + 1; j < this.weights.length; j++ ) {
                let diff = this.weights[i].add(this.weights[j].scale(-1.));
                let w = this.find_rep(this.braid_weights[idx].rep_obj());
                w.move_to( { x: diff.x, y: diff.y, z: diff.z } );
                w.arrow.setLength(w.length(), .15, .05);
                idx += 1;
            }
        }
        this._weights.add(this.braid_group);
    }

    hideBraid ( ) {
        this.braid_group.visible = false;
        this.braid_weights.forEach( function (c) {
            c.label.visible = false;
        });
        this.regionLabels.children.forEach( function (c) {
            c.visible = false;
        });
    }

    showBraid ( ) {
        this.braid_group.visible = true;
        this.braid_weights.forEach( function (c) {
            c.label.visible = true;
        });
        this.regionLabels.children.forEach( function (c) {
            c.visible = true;
        });
    }

	computeSignString ( p, ws ) {
		let sign_vector = [];
		ws.forEach(function (w) {
			let dot = p[0] * w.x + p[1] * w.y;
			if ( dot > 0 ) {
				sign_vector.push(1);
			}
			else if ( dot < 0 ) {
				sign_vector.push(0);
			}
			else {
				console.log('This is unlucky - point exactly on hyperplane');
			}
		});
		return sign_vector.join('');
	}

    getValidRegions ( weights ) {
        // This is hacky - only works for 2d with no offsets.
        // sensitivity of .5 degrees
        const radius = 2.3;
        const samples = 360 * 2;
        let valid_regions = new Set();
        if ( this.regionLabels !== undefined ) {
            this.braid_group.remove(this.regionLabels);
            let children = [...this.regionLabels.children];
            for ( let child of children ) {
                this.regionLabels.remove(child);
            }
        }
        this.regionLabels = new THREE.Group();

		// n-1 positions for n chambers
		let positions = [];
		const firstSign = this.computeSignString( [1, 0], weights );
		let stringSigns = [];
		let prevSign = firstSign;

        for (let i = 0; i < samples; i++) {
            // go around in a circle
            let x = Math.cos(2 * Math.PI * i / samples) * radius;
            let y = Math.sin(2 * Math.PI * i / samples) * radius;
            // dot product
			const signString = this.computeSignString( [x, y], weights );
			if ( signString != prevSign ) {
				positions.push( [x, y] );
				stringSigns.push( signString );
				valid_regions.add(signString);
				prevSign = signString;
			}
		}
		const numSigns = stringSigns.length;
        for (let i = 0; i < numSigns; i++) {
			const signString = stringSigns[i];
			const regionNumber = perm.vertexNumber[signString];
			const region = new RegionLabel(signString, colors.colors, regionNumber);
			const [xp, yp] = positions[i];
			const [xn, yn] = positions[(i + 1) % numSigns];
			let position;
			if ( stringSigns.length == 2 ) {
				if ( i == 0 ) {
					position = new THREE.Vector3(weights[0].x , weights[0].y, 0);
				}
				else {
					position = new THREE.Vector3(-weights[0].x , -weights[0].y, 0);
				}
			}
			else {
				position = new THREE.Vector3((xn + xp) / 2, (yn + yp) / 2, 0);
			}
			position = position.normalize();
			position = position.multiplyScalar(radius);
			const elem = region.buildLabel(); 
			let obj = new CSS2DObject( elem );
			obj.visible = this.braid_group.visible;
			obj.position.copy( position );
			this.regionLabels.add( obj );
            // and fetch number of region from perm number dict.
            // also add R_num
            this.braid_group.add(this.regionLabels);
        }
        return valid_regions;
    }

    classify ( dataset ) {
        let softmax_probs = [];
        for (let p of dataset.points) {
            const px = p.position.x;
            const py = p.position.y;
            const pz = p.position.z;
            let scores = [];
            for (let w of this.weights) {
                // let dot = px * w.x + py * w.y + pz * wz;
                let dot = px * w.x + py * w.y + pz * w.z;
                scores.push(dot);
            }
            let probs = MATH.softmax ( scores );
            softmax_probs.push(probs);
        }
        return softmax_probs;
    }

    get_direction( ) {
        let directions = [[], []]
        for (let w of this.weights ) {
            directions[0].push(w.x)
            directions[1].push(w.y)
        }
        // Compute 3D projection of 4D directions
        // (translate 2D plane from 4D to 3D)
        // This is currently the "projection" used for the simplex
        let projection = [[ 1.,  1.,  1.],
                          [ -1., -1., 1.],
                          [ -1.,  1.,  -1.],
                          [ 1.,  -1.,  -1.]];
        let transpose = [[], [], []];
        for (let i = 0; i < projection.length; i++ ) {
            for (let j = 0; j < projection[0].length; j++ ) {
                transpose[j].push(projection[i][j]);
            }
        }
        let dirs = [];

        for ( let i=0; i < directions.length; i++ ) {
            let xx = MATH.dotprod(transpose[0], directions[i]);
            let yy = MATH.dotprod(transpose[1], directions[i]);
            let zz = MATH.dotprod(transpose[2], directions[i]);
            let vec = new THREE.Vector3(xx, yy, zz);
            dirs.push(vec);
        }
        let cross = new THREE.Vector3();
        cross.crossVectors(dirs[0], dirs[1]);
        return cross
    }
}


function decideMouseView ( mouse ) {

    let targetView;
    const x = mouse.x;
    const y = window.innerHeight - mouse.y;

    views.forEach(function (view) {

        const left = window.innerWidth * view.left;
        const bottom = window.innerHeight * view.bottom;
        const width = window.innerWidth * view.width;
        const height = window.innerHeight * view.height;

        if ( (x > left) && (x < (left + width)) ) {
            if ( (y > bottom) && (y < (bottom + height)) ) {
                targetView = view;
            }
        }
    });

    return targetView;
}

function classifyPoints( ) {
    ws.braid_update();
    braid = ws.braid_weights;
    let labels = ws.getValidRegions( braid );
    perm.color_vertices(colors.black, .6, false);
    for (let label of labels) {
        let region = new RegionLabel(label);
        let v = perm.vertexLookup[label];
        if ( v !== undefined ) {
            let color = colors.colors[region.perm[region.perm.length - 1]];
            perm.color_vertex(v, color, 1., true);
        }
    }
    let probs = ws.classify(dataset);
    // ffs. argmax
    for ( let i = 0; i < dataset.length(); i++) {
        let scores = probs[i];
        let argm = MATH.argmax( scores );
        // set winner's color to data point
        dataset.points[i].material.color = new THREE.Color(ws.weights[argm].color);
        // console.log(dataset.points[i].material.color);
    }
    if ( simplexPoints !== undefined ) {
        views[1].scene.remove(simplexPoints);
    }
    simplexPoints = simplex.plot_project( probs );
    views[1].scene.add(simplexPoints);
    for (let region of Object.values(simplex.barycentricRegions)) {
        region.material.opacity = .01;
        region.material.depthWrite = false;
    }
    for (let label of labels) {
        let region = new RegionLabel(label);
        let br = simplex.barycentricRegions[region.perm.join(',')];
        br.material.opacity = .2;
    }
    if (dimension == 4) {
        let vertical = ws.get_direction();
        let vert_coords = [vertical.x, vertical.y, vertical.z];
        let norm_vertical = MATH.normalize(vert_coords);
        norm_vertical = MATH.scale(norm_vertical, 1.);
        let origin = new THREE.Vector3(0., 0., 0.);
        views[2].camera.position.set(...norm_vertical);
        views[2].camera.lookAt(origin);
    }
}

function onMouseMove( event ) {

    // calculate mouse position in normalized device coordinates
    // (-1 to +1) for both components
    mouse_orig.x = event.clientX;
    mouse_orig.y = event.clientY;
    currentView = decideMouseView( {x: event.clientX, y: event.clientY} );

    if ( currentView !== undefined ) {
        mouse.x = ( (event.clientX - currentView.left) / ( currentView.width * window.innerWidth ) ) * 2 - 1;
        mouse.y = - ( (event.clientY - currentView.bottom) / ( currentView.height * window.innerHeight ) ) * 2 + 1;

        if ( selected !== null ) {
            const x = mouse.x * currentView.camera.position.z;
            const y = mouse.y * currentView.camera.position.z;
            selected.move_to({x: x, y: y, z: 0});
            classifyPoints();
        }
    }
}

function onMouseDown( event ) {
    dragging = true;
    selected = ws.pick(mouse, views[0].scene, views[0].camera);
}

function onMouseUp( event ) {
    dragging = false;
    selected = null;
}

function render() {
    views.forEach(function (view) {
        const left = Math.floor( window.innerWidth * view.left );
        const bottom = Math.floor( window.innerHeight * view.bottom );
        const width = Math.floor( window.innerWidth * view.width );
        const height = Math.floor( window.innerHeight * view.height );

        renderer.setViewport( left, bottom, width, height );
        renderer.setScissor( left, bottom, width, height );
        renderer.setScissorTest( true );
        renderer.setClearColor( view.background );
        renderer.render( view.scene, view.camera );

        // view.point_light.position.copy( view.camera.position );

        labelRenderer.setSize(left, window.innerHeight - height - bottom, width, height);
        labelRenderer.render( view.scene, view.camera );
    });
}


function animate() {
    requestAnimationFrame(animate);
    currentView = decideMouseView( mouse_orig );
    if ( currentView !== undefined ) {
        if ( currentView.controls !== undefined ) {
            currentView.controls.update();
        }
    }
    render();
}


window.addEventListener('resize', () => {

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio( window.devicePixelRatio );

    // labelRenderer.setSize(window.innerWidth, window.innerHeight);

    views.forEach(function (v) {
        v.camera.aspect = ( v.width * window.innerWidth ) / ( v.height * window.innerHeight );
        v.camera.updateProjectionMatrix();
    });
});

window.addEventListener( 'pointerup', onMouseUp );
window.addEventListener( 'pointerdown', onMouseDown );
window.addEventListener( 'pointermove', onMouseMove );


function fetchDim ( ) {
	let span = document.getElementById('dim');
	let value = parseInt(span.innerHTML);
	return value;
}


document.getElementById('increment').addEventListener( 'click', function ( ) {
	let span = document.getElementById('dim');
	let value = fetchDim();
	if ( value < 4 ) {
		dimension = value + 1;
		span.innerHTML = dimension;
		cleanUp();
		init(dimension);
	}
});


document.getElementById('decrement').addEventListener( 'click', function ( ) {
	let span = document.getElementById('dim');
	let value = fetchDim();
	if ( value > 2 ) {
		dimension = value - 1;
		span.innerHTML = dimension;
		cleanUp();
		init(dimension);
	}
});

let dragging = false;
let selected = null;
let colors = new Colors(); 

let renderer, labelRenderer;

const mathjax = new RegionNums( MATH.factorial( 4 ) );
const mj = mathjax.generateMathJax();
document.getElementById("permanent").appendChild(mj);

let container = document.getElementById("container");

let dimension = fetchDim();

let views, currentView;
let dataset, ws;
// let ww;
let simplex, perm, braid;
let simplexPoints;

const mouse = new THREE.Vector2();
const mouse_orig = new THREE.Vector2();


function cleanUp() {
	let children = [...container.children];
	for ( let c of children ) {
		if ( c.id !== 'permanent' ) {
			container.removeChild(c);
		}
	}
	for ( let v of views ) {
		let children = [...v.scene.children];
		for ( let c of children ) {
			v.scene.remove(c);
		}
	}
}


function init(dim) {

	renderer = new THREE.WebGLRenderer({antialias: true});
	renderer.setClearColor("#e5e5e5");
	renderer.setSize(window.innerWidth, window.innerHeight);
	renderer.setPixelRatio( window.devicePixelRatio );

	labelRenderer = new CSS2DRenderer();
	labelRenderer.setSize( window.innerWidth, window.innerHeight );
	labelRenderer.domElement.style.position = 'absolute';
	labelRenderer.domElement.style.top = '0px';
	labelRenderer.domElement.style.pointerEvents = 'none';

	container.appendChild(renderer.domElement);
	container.appendChild(labelRenderer.domElement);

	const braidButton = document.createElement( 'button' );
	braidButton.active = false;
	braidButton.id = 'data-button';
	braidButton.innerHTML = 'Show Braid Arrangement';

	container.appendChild( braidButton );

    views = [
        {
            left: 0,
            bottom: 0,
            width: 0.6,
            height: 1.0,
            background: new THREE.Color(0x191919),
            eye: [ .0002, .0001, 3.5 ],
            up: [ 0, 1, 0 ],
            fov: 80,
        },
        {
            left: 0.6,
            bottom: 0,
            width: 0.4,
            height: 0.5,
            background: new THREE.Color( 0xe1ddef ),
            eye: [ .2, 4, 2. ],
            up: [ 1, 1, 1 ],
            fov: 50,
        },
        {
            left: 0.6,
            bottom: 0.5,
            width: 0.4,
            height: 0.5,
            background: new THREE.Color( 0xf6e1e1 ),
            // background: new THREE.Color( 0xedd8d8 ),
            eye: [ -1, 5, 2 ],
            up: [ 1, 1, 1 ],
            fov: 70,
        }
    ];


    for ( let ii = 0; ii < views.length; ++ ii ) {

        const view = views[ ii ];
        let camera;
        if ( ii !== 2 ) {
            camera = new THREE.PerspectiveCamera( view.fov, (view.width * window.innerWidth) / (view.height * window.innerHeight), 0.1, 1000 );
        }
        else {
            const height = view.height * window.innerHeight;
            const width = view.width * window.innerWidth;
            const scale = 125;
            camera = new THREE.OrthographicCamera( width / -scale, width / scale, height / scale, height / -scale , -100, 100 );
        }
        camera.position.fromArray( view.eye );
        camera.up.fromArray( view.up );
        if ( ii != 0 ) {
            view.controls = createControls( camera );
        }
        view.camera = camera;
        view.scene = new THREE.Scene();
        const point_light = new THREE.PointLight( 0x3a3a3a, 5, 1000 );
        point_light.position.set( 10, 10, 10 );
        view.point_light = point_light;
        view.scene.add(point_light);
        const ambient_light = new THREE.AmbientLight( 0xa1a1a1 );
        view.ambient_light = ambient_light;
        view.scene.add( ambient_light );
    }

    const basis = [new WeightVector(0, -1, 0, colors.colors[0], 'Class a'),
                   new WeightVector(1, 1, 0, colors.colors[1], 'Class b'),
                   new WeightVector(.2, 1, 0, colors.colors[2], 'Class c'),
                   new WeightVector(-1, 0, 0, colors.colors[3], 'Class d'),
    ].slice(0, dim);

    ws = new WeightQuiver(basis);
    ws.get_direction();
    ws.braid_group.visible = braidButton.active;

    ws.add_to(views[0].scene);

    // ww = new WeightVector(0, 0, 0);
    // ww.add_to(views[1].scene);
    // ww.set_hyperplane_visible();

    const NUM_ROWS = 50 * dim;
    const data = [];
    for ( let j = 0; j < NUM_ROWS; j++ ) {
        let row = [];
        for ( let i = 0; i < 2; i++ ) {
            row.push((Math.random() - .5) * 5);
        }
        row.push(-.1);
        data.push(row);
    }

    dataset = new Dataset(data, .02);
    dataset.add_to(views[0].scene);

    ws.classify( dataset );

    perm = new Permutohedron([0, 0, 0], dim);
    perm.add_to(views[2].scene);

    simplex = new Simplex([0, 0, 0], dim, .9, colors.colors);
    // views[1].scene.add(simplex.polytope);
    views[1].scene.add(simplex.vertices);
    views[1].scene.add(simplex.constructBarycentric());
    // simplex.add_to(views[1].scene);

    classifyPoints();

    ws.hideBraid();

    braidButton.addEventListener( 'click', function () {
        if ( !braidButton.active ) {
            ws.showBraid();
            braidButton.active = true;
            braidButton.innerHTML = 'Hide Braid Arrangement';
        }
        else {
            ws.hideBraid();
            braidButton.active = false;
            braidButton.innerHTML = 'Show Braid Arrangement';
        }
    }, false);

    animate();
}

init(dimension);
